import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { AllBusiness, AllCustomerShopping } from '../../services/api';
import AdminHeader from './AdminHeader';
import Pagination from '../common/Pagination';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
const ITEMS_PER_PAGE = 10;

const AdminBusinessList = ( showBack = true) => {
  const [loading, setLoading] = useState(true);
  const [businesses, setBusinesses] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('businessName');
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        // Prefer API
        try {
          const resp = await AllBusiness({});
          const list = Array.isArray(resp.extractedData) ? resp.extractedData : (resp.extractedData?.records || []);

          // Fetch transactions to compute availablePoints (sum of point_balance)
          // and totalPurchases (sum of amount) per business
          let txAgg = {};
          try {
            const txResp = await AllCustomerShopping({});
            const txList = Array.isArray(txResp.extractedData) ? txResp.extractedData : (txResp.extractedData?.records || []);
            txAgg = txList.reduce((acc, t) => {
              // Primary join key is email_id per requirement; normalize to lowercase
              const rawKey = t.email_id || t.email || t.businessEmail || t.business_email || t.vendorEmail || t.vendor_email || t.profile_id || t.profileId || null;
              const key = typeof rawKey === 'string' ? rawKey.trim().toLowerCase() : rawKey;
              if (!key) return acc;
              if (!acc[key]) acc[key] = { totalAmount: 0, pointBalanceSum: 0 };
              const amt = Number(t.amount ?? t.total ?? 0);
              const pb = Number(t.point_balance ?? t.pointBalance ?? 0);
              if (isFinite(amt)) acc[key].totalAmount += amt;
              if (isFinite(pb)) acc[key].pointBalanceSum += pb;
              return acc;
            }, {});
          } catch (e) {
            // If transaction API fails, leave txAgg empty and fall back to zeros
            txAgg = {};
          }

          const rows = list
            .filter((u) => {
              const role = u.role?.toLowerCase?.();
              const reg = u.registrationType?.toLowerCase?.();
              const hasShops = Array.isArray(u.shopDetails) && u.shopDetails.length > 0;
              return role === 'business' || (reg && reg !== 'customer') || hasShops;
            })
            .map((u, idx) => ({
              id: u.profile_id || u.id || String(idx),
              businessName: u.businessName || (u.firstName ? `${u.firstName} ${u.lastName||''}`.trim() : (u.displayName || u.name || 'Business')),
              email: u.email || u.email_id || '',
              availablePoints: (() => {
                const keyEmail = (u.email || u.email_id || '').toLowerCase();
                if (keyEmail && txAgg[keyEmail]) return txAgg[keyEmail].pointBalanceSum || 0;
                return 0;
              })(),
              totalPurchases: (() => {
                const keyEmail = (u.email || u.email_id || '').toLowerCase();
                if (keyEmail && txAgg[keyEmail]) return txAgg[keyEmail].totalAmount || 0;
                return 0;
              })(),
              totalIncome: u.totalIncome ?? 0,
              createdAt: (() => {
                const v = u.created_on;
                if (!v) return null;
                if (typeof v === 'string' || typeof v === 'number') return new Date(v);
                if (v.$date) return new Date(v.$date);
                return null;
              })(),
              phone: u.phone || '',
              address: u.address || '',
            }));
          setBusinesses(rows);
          setFiltered(rows);
        } catch (apiErr) {
          // Fallback to Firestore
          const snap = await getDocs(collection(db, 'users'));
          const rows = [];
          snap.forEach((doc) => {
            const d = doc.data();
            if (d.role === 'business') {
              rows.push({
                id: doc.id,
                businessName: d.businessName || d.displayName || 'Business',
                email: d.email || '',
                availablePoints: d.availablePoints ?? 0,
                totalPurchases: d.totalPurchases ?? 0,
                totalIncome: d.totalIncome ?? 0,
                createdAt: d.createdAt ? (typeof d.createdAt?.toDate === 'function' ? d.createdAt.toDate() : new Date(d.createdAt)) : null,
              });
            }
          });
          setBusinesses(rows);
          setFiltered(rows);
        }
      } catch (e) {
        // Offline fallback demo data
        const rows = [
          { id: 'b1', businessName: 'ABC Store', email: 'abc@store.com', availablePoints: 1200, totalPurchases: 45, totalIncome: 32000, createdAt: new Date(Date.now() - 86400000) },
          { id: 'b2', businessName: 'XYZ Restaurant', email: 'xyz@resto.com', availablePoints: 800, totalPurchases: 31, totalIncome: 21000, createdAt: new Date() },
        ];
        setBusinesses(rows);
        setFiltered(rows);
      } finally {
        setLoading(false);
      }
    };
    fetchBusinesses();
  }, []);

  const handleBack = () => {
    // if (onBack) return onBack();
    navigate('/admin/dashboard');
  };

  useEffect(() => {
    let data = [...businesses];
    // search by business name or email
    if (search) {
      const q = search.toLowerCase();
      data = data.filter((b) => (b.businessName || '').toLowerCase().includes(q) || (b.email || '').toLowerCase().includes(q));
    }
    // date range filter
    if (dateFrom) {
      const from = new Date(dateFrom);
      data = data.filter((b) => !b.createdAt || b.createdAt >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo);
      // include end of day
      to.setHours(23, 59, 59, 999);
      data = data.filter((b) => !b.createdAt || b.createdAt <= to);
    }
    // sorting
    const dir = sortDir === 'asc' ? 1 : -1;
    data.sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') return av.localeCompare(bv) * dir;
      if (av instanceof Date) return ((av) - (bv)) * dir;
      return (Number(av) - Number(bv)) * dir;
    });
    setFiltered(data);
    setCurrentPage(1);
  }, [search, dateFrom, dateTo, sortBy, sortDir, businesses]);

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const { pageItems, totalPages } = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return {
      pageItems: filtered.slice(start, end),
      totalPages: Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE)),
    };
  }, [filtered, currentPage]);

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader title="Business List" showBack onBack={handleBack} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      
        {/* Filters */}
        <div>
        {showBack && (
              <button
                onClick={handleBack}
                className="flex items-center space-x-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-black px-3 py-2 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back</span>
              </button>
            )}
        </div>
        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search business or email..."
              className="border border-gray-300 rounded-lg px-3 py-2"
            />
            <div>
              <label className="block text-xs text-gray-600 mb-1">From</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">To</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>
            <button onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); }} className="self-end bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg">Clear</button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th onClick={()=>toggleSort('businessName')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Business {sortBy==='businessName' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  <th onClick={()=>toggleSort('email')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Email {sortBy==='email' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  <th onClick={()=>toggleSort('availablePoints')} className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase cursor-pointer">Available Points {sortBy==='availablePoints' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  <th onClick={()=>toggleSort('totalPurchases')} className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase cursor-pointer">Total Purchases {sortBy==='totalPurchases' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  {/* <th onClick={()=>toggleSort('totalIncome')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Total Income {sortBy==='totalIncome' ? (sortDir==='asc'?'▲':'▼') : ''}</th> */}
                  <th onClick={()=>toggleSort('createdAt')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Created {sortBy==='createdAt' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr><td className="px-6 py-8 text-center text-gray-500" colSpan={6}>Loading...</td></tr>
                ) : pageItems.length === 0 ? (
                  <tr><td className="px-6 py-8 text-center text-gray-500" colSpan={6}>No businesses found</td></tr>
                ) : (
                  pageItems.map((b) => (
                    <tr key={b.id} onClick={() => navigate(`/admin/business/${b.id}`, { state: { email: b.email, profileId: b.id } })} className="hover:bg-gray-50 cursor-pointer">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{b.businessName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{b.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{Number(b.availablePoints || 0).toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">₹{Number(b.totalPurchases || 0).toLocaleString()}</td>
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₹{Number(b.totalIncome).toLocaleString()}</td> */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{b.createdAt ? b.createdAt.toLocaleDateString() : '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4">
            <Pagination
              currentPage={currentPage}
              totalPages={Math.max(1, totalPages)}
              onPageChange={(p) => setCurrentPage(Math.max(1, Math.min(p, totalPages)))}
              itemsPerPage={ITEMS_PER_PAGE}
              totalItems={filtered.length}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBusinessList;
